import { PaneGroupContext } from "./PaneGroupContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useContext, type ReactNode } from "react";

export const PaneOverlay = ({
  children,
  open,
  presentation,
  width,
  ariaLabel,
  onClose,
}: {
  children: ReactNode;
  open: boolean;
  presentation: "floating" | "fullscreen";
  width?: number | string;
  ariaLabel?: string;
  onClose: () => void;
}) => {
  const group = useContext(PaneGroupContext);
  const isFloating = presentation === "floating";
  const container = isFloating ? group?.container : undefined;
  if (isFloating && group && !container) return null;

  return (
    <Sheet
      open={open}
      modal={presentation === "fullscreen"}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        noClose
        overlay={false}
        container={container}
        className={cn(
          "m-0 flex max-w-full flex-col rounded-none p-0 transition-none sm:max-w-none",
          container ? "absolute h-full" : "h-dvh"
        )}
        style={{
          width: presentation === "fullscreen" ? "100%" : (width ?? 440),
          animation: "none",
        }}
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          // Ant Design selects handle Escape on their input, outside Radix's popup stack.
          if (
            event.target instanceof Element &&
            event.target.closest('[role="combobox"][aria-expanded="true"]')
          )
            event.preventDefault();
        }}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <SheetTitle className="sr-only">{ariaLabel}</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  );
};
